import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/utils/Loading";
import MaterialsCatalogList from "./MaterialsCatalogList";
import { fetchMaterialsCatalog } from "../waste/wasteSortingItems";
import {
  filterCatalogMaterials,
  getCatalogMaterials,
  STREAM_HOUSEHOLD,
  STREAM_INDUSTRIAL,
} from "./materialsCatalog";
import { downloadLegendCsv, downloadLegendJson } from "./materialsLegendExport";

const MaterialsCatalogPage = ({ token, API, basePath }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(STREAM_INDUSTRIAL);
  const [isLoading, setIsLoading] = useState(true);
  const [rawCatalog, setRawCatalog] = useState([]);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [detectionFilter, setDetectionFilter] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setIsLoading(true);
      const catalog = await fetchMaterialsCatalog(API, token);
      if (cancelled) return;
      setRawCatalog(Array.isArray(catalog) ? catalog : []);
      setIsLoading(false);
    };

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [API, token]);

  useEffect(() => {
    setKeywordFilter("");
    setCategoryFilter("");
    setDetectionFilter("");
  }, [activeTab]);

  const materials = useMemo(
    () => getCatalogMaterials(rawCatalog, activeTab, t),
    [rawCatalog, activeTab, t],
  );

  const filteredMaterials = useMemo(
    () =>
      filterCatalogMaterials(materials, {
        keyword: keywordFilter,
        category: categoryFilter,
        detection: detectionFilter,
      }),
    [materials, keywordFilter, categoryFilter, detectionFilter],
  );

  const tabs = [
    {
      id: STREAM_INDUSTRIAL,
      label: t("materialsCatalog.tabIndustrial"),
    },
    {
      id: STREAM_HOUSEHOLD,
      label: t("materialsCatalog.tabHousehold"),
    },
  ];

  const actionsMenuItems =
    materials.length > 0
      ? [
          {
            label: t("materialsCatalog.exportCsv"),
            onClick: () => downloadLegendCsv(filteredMaterials, t),
          },
          {
            label: t("materialsCatalog.exportJson"),
            onClick: () => downloadLegendJson(filteredMaterials),
          },
        ]
      : [];

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("materialsCatalog.title")}
        subtitle={t("materialsCatalog.subtitle")}
        showBackButton
        backRoute={basePath}
        backLabel={t("settingsWaste.backToFlows")}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actionsMenuItems={actionsMenuItems}
      />
      {isLoading ? (
        <Loading color="#F5F5F5" />
      ) : (
        <MaterialsCatalogList
          materials={materials}
          filteredMaterials={filteredMaterials}
          keywordFilter={keywordFilter}
          onKeywordChange={setKeywordFilter}
          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}
          detectionFilter={detectionFilter}
          onDetectionChange={setDetectionFilter}
        />
      )}
    </div>
  );
};

export default MaterialsCatalogPage;
